<i18n>
[
    "browse__brand--label",
]
</i18n>

<template>
    <CatalogLayout
        class="c-browse"
        :pageHeader="pageHeader"
        :contentfulTitle="contentfulTitle"
        :contentfulStartTime="contentfulStartTime"
        :contentfulEndTime="contentfulEndTime"
        :contentfulTopper="contentfulTopper"
        :contentfulFooter="contentfulBottomContent"
        :contentfulInGridContentRows="contentfulInGridContentRows"
        :contentfulCurationNavItems="contentfulCurationNavItems"
        :contentfulFaceOutImage="contentfulFaceOutImage"
        :products="products"
        :filterGroups="filterGroups"
        :catalogContext="catalogContext"
        :pagination="pagination"
        :sortOptions="sortOptions"
        :productListId="slug"
        :isPrePurchase="isPrePurchase"
        :selectedCuration="selectedCuration"
    />
</template>

<!-- Seperate script tag needed for custom fetchData and vuex logic, to be used in entry-server and entry-client -->
<script>
import { NAVIGATION_MODULE_NAME } from '~modules/nav/js/nav-store';
import { UNIVERSAL_REDIRECT } from '~coreModules/core/js/store';
import { genericRouteNames } from '~coreModules/core/js/router-constants';

import browseModule, {
    BROWSE_MODULE_NAME,
    FETCH_BROWSE_INFORMATION,
    FETCH_CURATION_INFORMATION,
    SET_SELECTED_CURATION,
} from '~routes/browse/js/browse-store';

async function fetchData({ store, route, router }) {
    const { getters, state } = store;
    const { loggedIn } = getters;
    const { slug } = route.params;
    const { flatNavigation } = state[NAVIGATION_MODULE_NAME];
    const navItem = flatNavigation[slug] || {};
    const isPrePurchase = !!navItem?.displayAsSale;
    const curationQuery = route.query?.curation;
    const sort = route.query?.sort;

    store.commit(`${BROWSE_MODULE_NAME}/${SET_SELECTED_CURATION}`, curationQuery || null);

    // pre purchase pages require user to be logged in
    if (isPrePurchase && !loggedIn) {
        await store.dispatch(UNIVERSAL_REDIRECT, {
            router,
            route: {
                name: genericRouteNames.login.phone.base,
            },
        });
    } else if (curationQuery) {
        const { adjustedSort = null } = await store.dispatch(`${BROWSE_MODULE_NAME}/${FETCH_CURATION_INFORMATION}`, {
            slug,
            route,
        });

        if (adjustedSort && sort !== adjustedSort) {
            await store.dispatch(UNIVERSAL_REDIRECT, {
                router,
                route: {
                    ...route,
                    query: {
                        ...route?.query,
                        sort: adjustedSort,
                    },
                },
            });
        }
    } else {
        await store.dispatch(`${BROWSE_MODULE_NAME}/${FETCH_BROWSE_INFORMATION}`, {
            slug,
            route,
            isPrePurchase,
        });
    }
}

export default {
    fetchData,
    vuex: {
        moduleName: BROWSE_MODULE_NAME,
        module: browseModule,
    },
};
</script>

<script setup>
/* eslint-disable import/first */
import { computed } from 'vue';
import { isEmpty } from 'lodash-es';
import { useI18n } from 'vue-i18n';

import { useState } from '~coreModules/core/js/composables/vuexHelpers';

import useBrowseAnalytics from '~routes/browse/js/composables/use-browse-analytics';
import useBrowseMeta from '~routes/browse/js/composables/use-browse-meta';

import { SITE_SETTINGS_MODULE_NAME } from '~modules/site-settings/js/site-settings-store';

import CatalogLayout from '~modules/core/components/layouts/CatalogLayout.vue';
/* eslint-disable import/first */

const { t } = useI18n();
useBrowseAnalytics();
useBrowseMeta();

const {
    slug,
    products,
    filterGroups,
    sortOptions,
    contentfulTitle,
    contentfulStartTime,
    contentfulEndTime,
    contentfulTopContent,
    contentfulBottomContent,
    contentfulInGridContentRows,
    contentfulCurationNavItems,
    contentfulFaceOutImage,
    browsePageHeader,
    pagination,
    brandName,
    isPrePurchase,
    selectedCuration,
} = useState(BROWSE_MODULE_NAME, {
    slug: state => state.catalog.browseSlug,
    products: state => state.catalog.products,
    filterGroups: state => state.catalog.filterGroups,
    sortOptions: state => state.catalog.sortOptions,
    contentfulTitle: state => state.contentfulContent?.title,
    contentfulStartTime: state => state.contentfulContent?.startTime,
    contentfulEndTime: state => state.contentfulContent?.endTime,
    contentfulTopContent: state => state.contentfulContent?.topContent,
    contentfulBottomContent: state => state.contentfulContent?.bottomContent,
    contentfulInGridContentRows: state => state.contentfulContent?.inGridContent,
    contentfulCurationNavItems: state => state.contentfulContent?.curationNavItems,
    contentfulFaceOutImage: state => state.contentfulContent?.faceOut,
    browsePageHeader: state => state.contentfulContent?.headerConfiguration,
    pagination: state => state.catalog.pagination,
    brandName: state => state.brandName,
    isPrePurchase: state => state.isPrePurchase,
    selectedCuration: state => state.selectedCuration,
});

const { defaultBrandHeader } = useState(SITE_SETTINGS_MODULE_NAME, {
    defaultBrandHeader: state => state.contentfulAppConfig.brandHeader,
});

const catalogContext = BROWSE_MODULE_NAME;

const showDefaultBrandHeader = computed(() => Boolean(brandName.value && isEmpty(contentfulTopContent.value)));
const contentfulTopper = computed(() => (showDefaultBrandHeader.value ? null : contentfulTopContent.value));
const pageHeader = computed(() => {
    const brandHeader = {
        editorialText: {
            headline: brandName.value,
            label: t('browse__brand--label'),
            textColor: defaultBrandHeader.value?.editorialText?.textColor,
        },
        backgroundImage: defaultBrandHeader.value?.backgroundImage,
    };

    return showDefaultBrandHeader.value ? brandHeader : browsePageHeader.value;
});

</script>

<style lang="scss">

</style>
